if (!customElements.get('nosht-sport-bundle-form')) {
  customElements.define(
    'nosht-sport-bundle-form',
    class NoshtSportBundleForm extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[data-sport-bundle-submit]');
        this.errorWrapper = this.querySelector('[data-bundle-error]');
        this.errorMessage = this.errorWrapper?.querySelector('.product-form__error-message');
        this.status = this.querySelector('[data-bundle-status]');
        this.total = this.querySelector('[data-bundle-total]');
        this.totalAmount = this.querySelector('[data-bundle-total-amount]');

        if (!this.form || !this.submitButton) return;

        this.controller = new AbortController();
        this.form.addEventListener('submit', this.onSubmit, { signal: this.controller.signal });
        this.form.addEventListener('change', this.onSelectionChange, { signal: this.controller.signal });
        this.updateTotal();
        this.initialized = true;
      }

      onSelectionChange = (event) => {
        if (event.target.matches('[data-bundle-variant]')) this.updateTotal();
      };

      getSelectedTotal() {
        const controls = Array.from(this.querySelectorAll('[data-bundle-variant]'));
        const expectedItemCount = Number(this.dataset.expectedItemCount);
        if (this.dataset.bundleReady !== 'true' || !Number.isSafeInteger(expectedItemCount) || expectedItemCount <= 0 || controls.length !== expectedItemCount) return null;

        const productIds = new Set();
        let total = 0;
        for (const control of controls) {
          const selected = control.tagName === 'SELECT' ? control.selectedOptions[0] : control;
          const priceText = selected?.dataset.price;
          const price = Number(priceText);
          const variantId = Number(control.value);
          const productId = Number(control.dataset.productId);
          if (!selected || selected.dataset.available !== 'true' || (control.tagName === 'SELECT' && selected.disabled) ||
            priceText === undefined || priceText.trim() === '' || !Number.isSafeInteger(price) || price < 0 ||
            !Number.isSafeInteger(variantId) || variantId <= 0 || !Number.isSafeInteger(productId) || productId <= 0 || productIds.has(productId)) return null;

          productIds.add(productId);
          total += price;
          if (!Number.isSafeInteger(total)) return null;
        }
        return total;
      }

      updateTotal() {
        if (!this.total || !this.totalAmount) return;
        const total = this.getSelectedTotal();
        this.total.hidden = total === null;
        if (total !== null) {
          const formattedTotal = this.formatTotal(total);
          this.total.hidden = formattedTotal === null;
          this.totalAmount.textContent = formattedTotal ?? '';
        } else {
          this.totalAmount.textContent = '';
        }

        // Another set may be submitting while this set's choices change.
        // Keep the shared submission lock, updating only its eventual state.
        if (this.submitButton.dataset.bundlePreviouslyDisabled !== undefined) {
          this.submitButton.dataset.bundlePreviouslyDisabled = String(total === null);
        } else {
          this.submitButton.disabled = total === null;
        }
        this.submitButton.setAttribute('aria-disabled', String(this.submitButton.disabled));
      }

      formatTotal(total) {
        // Derive separators and currency placement from Shopify's own Liquid
        // output. Prices are already in presentment currency; never convert again.
        const template = document.createElement('template');
        template.innerHTML = this.dataset.moneyExample || '';
        const example = template.content.textContent || '';
        const match = example.match(/\d(?:[\d.,'’\s]*\d)?/);
        if (!match) return null;

        const numeric = match[0];
        const digits = numeric.replace(/\D/g, '');
        const decimals = digits === '123456789' ? 2 : digits === '1234568' ? 0 : null;
        if (decimals === null) return null;

        const decimalSeparator = decimals ? numeric.at(-3) : '';
        if (decimals && !/^[.,]$/.test(decimalSeparator)) return null;
        const integerPattern = decimals ? numeric.slice(0, -3) : numeric;
        const separators = integerPattern.replace(/\d/g, '');
        const groupSeparator = separators[0] || '';
        if (separators !== groupSeparator.repeat(separators.length)) return null;
        if (groupSeparator && !/^1[^\d]234[^\d]56[78]$/.test(integerPattern)) return null;

        const cents = BigInt(total);
        const whole = (decimals ? cents / 100n : (cents + 50n) / 100n).toString();
        const grouped = groupSeparator ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator) : whole;
        const fraction = decimals ? decimalSeparator + (cents % 100n).toString().padStart(2, '0') : '';
        return example.slice(0, match.index) + grouped + fraction + example.slice(match.index + numeric.length);
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.requestController?.abort();
        this.controller = null;
        this.requestController = null;
        this.initialized = false;
      }

      onSubmit = async (event) => {
        event.preventDefault();
        if (this.getAttribute('aria-busy') === 'true' || this.submitButton.disabled) return;

        const items = this.getSelectedItems();
        const controls = Array.from(this.querySelectorAll('[data-bundle-variant]'));
        const expectedItemCount = Number.parseInt(this.dataset.expectedItemCount, 10);
        const selectedItemCount = items.reduce((total, item) => total + item.quantity, 0);
        const productIds = controls.map((control) => Number.parseInt(control.dataset.productId, 10));
        const hasInvalidControl = controls.some((control, index) => {
          const variantId = Number.parseInt(control.value, 10);
          return !Number.isSafeInteger(variantId) || variantId <= 0 || !Number.isSafeInteger(productIds[index]) || productIds[index] <= 0;
        });
        const hasDuplicateProduct = new Set(productIds).size !== productIds.length;
        const selectionIsIncomplete =
          !Number.isSafeInteger(expectedItemCount) ||
          expectedItemCount <= 0 ||
          selectedItemCount !== expectedItemCount ||
          hasInvalidControl ||
          hasDuplicateProduct;

        if (items.length === 0 || selectionIsIncomplete) {
          this.showError(this.dataset.errorText || 'No available products were selected.');
          return;
        }

        this.clearMessages();
        this.setLoading(true);
        this.setStatus(this.dataset.loadingText || 'Adding the set to your cart…');
        this.requestController = new AbortController();

        let response;
        let payload = {};

        try {
          const cartAddUrl = `${window.routes?.cart_add_url || '/cart/add'}`.replace(/\.js$/, '') + '.js';
          response = await fetch(cartAddUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items: items.map(({ id, quantity }) => ({ id, quantity }))
            }),
            signal: this.requestController.signal
          });

          payload = await response.json().catch(() => ({}));

          if (!response.ok) {
            const serverMessage = payload.description || payload.message;
            const fallbackMessage = this.dataset.errorText || 'Your cart may have been updated. Please check it and try again.';
            throw new Error(serverMessage ? `${fallbackMessage} ${serverMessage}` : fallbackMessage);
          }

          document.dispatchEvent(
            new CustomEvent('ajaxProduct:added', {
              detail: {
                productId: items[0].productId,
                productIds: items.map((item) => item.productId),
                source: 'nosht-sport-bundles',
                openCart: this.dataset.openCart === 'true'
              }
            })
          );

          this.setStatus(this.dataset.successText || 'The set was added to your cart.');
        } catch (error) {
          if (error.name === 'AbortError') return;

          this.showError(error.message || this.dataset.errorText || 'Your cart may have been updated. Please check it and try again.');
          await this.synchronizeCartAfterError();
        } finally {
          this.requestController = null;
          this.setLoading(false);
        }
      };

      getSelectedItems() {
        const itemsByVariant = new Map();

        this.querySelectorAll('[data-bundle-variant]').forEach((control) => {
          const id = Number.parseInt(control.value, 10);
          const productId = Number.parseInt(control.dataset.productId, 10);
          if (!Number.isSafeInteger(id) || id <= 0) return;

          const existing = itemsByVariant.get(id);
          if (existing) {
            existing.quantity += 1;
          } else {
            itemsByVariant.set(id, {
              id,
              productId: Number.isSafeInteger(productId) ? productId : undefined,
              quantity: 1
            });
          }
        });

        return Array.from(itemsByVariant.values());
      }

      setLoading(isLoading) {
        const section = this.closest('.nosht-sport-bundles');
        const buttons = Array.from(section?.querySelectorAll('[data-sport-bundle-submit]') || [this.submitButton]);

        buttons.forEach((button) => {
          if (isLoading) {
            button.dataset.bundlePreviouslyDisabled = String(button.disabled);
            button.disabled = true;
          } else {
            button.disabled = button.dataset.bundlePreviouslyDisabled === 'true';
            delete button.dataset.bundlePreviouslyDisabled;
          }
          button.setAttribute('aria-disabled', String(button.disabled));
        });

        this.querySelectorAll('[data-bundle-variant]').forEach((control) => {
          control.disabled = isLoading;
        });

        this.toggleAttribute('aria-busy', isLoading);
        this.setAttribute('aria-busy', String(isLoading));
        this.submitButton.classList.toggle('loading', isLoading);
        this.submitButton.querySelector('.loading-overlay')?.classList.toggle('hidden', !isLoading);
        if (!isLoading) this.updateTotal();
      }

      clearMessages() {
        if (this.errorWrapper) this.errorWrapper.hidden = true;
        if (this.errorMessage) this.errorMessage.textContent = '';
        this.setStatus('');
      }

      showError(message) {
        if (this.errorMessage) this.errorMessage.textContent = message;
        if (this.errorWrapper) this.errorWrapper.hidden = false;
        this.setStatus('');
      }

      setStatus(message) {
        if (this.status) this.status.textContent = message;
      }

      async synchronizeCartAfterError() {
        document.dispatchEvent(new CustomEvent('cart:update'));

        const cartItems = document.querySelector('cart-items');
        if (typeof cartItems?.renderItems === 'function') {
          try {
            await cartItems.renderItems();
          } catch (error) {
            console.warn('NOSHT sport bundles: cart page refresh failed.', error);
          }
        }

      }
    }
  );
}
